<script setup>
import {ref, computed} from 'vue'
import draggable from 'vuedraggable'
import {useRoute, useRouter} from 'vue-router'
import {useComponentFieldsStore} from '@/stores/componentFields'
import {useItemDataStore} from '@/stores/itemData'
import DragHandle from '@/common/DragHandle'

const route = useRoute()
const router = useRouter()

const componentFieldsStore = useComponentFieldsStore()
const itemDataStore = useItemDataStore()

const isDragging = ref(false)
const ajaxInProgress = ref(false)
const dragImg = ref(null)

const initDragImage = () => {
    const img = new Image()
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
    dragImg.value = img
}
initDragImage()

const selectedId = computed(() => parseInt(route.params.fieldsetId))

const items = computed({
    get: () => componentFieldsStore.fieldsets,
    set: (value) => {
        componentFieldsStore.fieldsets = JSON.parse(JSON.stringify(value))
    }
})

const dragOptions = computed(() => ({
    animation: 200,
    ghostClass: 'ghost',
    setData: (dataTransfer) => {
        if (dragImg.value) {
            dataTransfer.setDragImage(dragImg.value, 0, 0)
        }
    }
}))

const editItem = (id) => {
    router.push({
        name: 'componentFields',
        params: {fieldsetId: id, fieldId: null}
    })
}

const onDragEnd = (obj) => {
    isDragging.value = false
    const item = items.value[obj.newIndex]

    ajaxInProgress.value = true
    itemDataStore.update({
            tableName: 'fieldset',
            id: item.id,
            data: {displayOrder: obj.newIndex + 1}
        })
        .then(() => {
            return componentFieldsStore.reloadFieldsets()
        })
        .finally(() => {
            ajaxInProgress.value = false
        })
}

const setFormData = (obj) => {
    componentFieldsStore.fieldsetFormData = JSON.parse(JSON.stringify(obj))
}
</script>

<template>
    <div>
        <div :class="{ 'content-type--items-is-dragging': isDragging }">
            <draggable
                v-bind="dragOptions"
                v-if="items.length"
                v-model="items"
                item-key="id"
                handle=".content-type--item-drag-handle"
                @start="isDragging = true"
                @end="onDragEnd"
                class="list-group"
            >
                <template #item="{ element: item }">
                    <a
                        :key="item.id"
                        ref="contentTypeItem"
                        :class="{ 'active': item.id === selectedId, 'disabled': ajaxInProgress }"
                        class="list-group-item ps-1 d-flex align-items-center"
                        href
                        @click.prevent="editItem(item.id)"
                    >
                        <DragHandle class="content-type--item-drag-handle"/>

                        <span class="flex-fill">{{ item.legend }}</span>

                        <i data-bs-toggle="offcanvas"
                           data-bs-target="#contentTypeFieldsetOffcanvas"
                           class="bi-pencil-square"
                           role="button"
                           aria-controls="contentTypeFieldsetOffcanvas"
                           @click.prevent.stop="setFormData(item)"
                        />
                    </a>
                </template>
            </draggable>
        </div>
    </div>
</template>

<style scoped>
.ghost {
    background: #666 !important;
    border-color: #666 !important;
    color: #fff !important;
}

.content-type--item-drag-handle {
    opacity: 0;
    color: #999;
}

.list-group-item:hover .content-type--item-drag-handle {
    opacity: 1;
}

.list-group-item i {
    opacity: 0;
}

.list-group-item:hover i {
    opacity: 1;
}

.content-type--items-is-dragging .content-type--item-drag-handle {
    opacity: 0 !important;
}
</style>